import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Card from "components/card/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import { IAccommodationAmenity } from "../amenities/Amenities";

export interface IAccommodationUnitType {
  id: number;
  title: string;
  base_occupancy: number;
  max_occupancy: number;
  category_id: number;
  price?: number;
  image_path?: string;
  image_url?: string;
  amenities?: IAccommodationAmenity[];
  available_units_count?: number;
}

export function UnitTypes() {
  const history = useHistory();
  const { t } = useTranslation();

  const [accommodationUnitTypes, setAccommodationUnitTypes] = useState(undefined);

  const [filterState, setFilterState] = useState({
    search: "",
    offset: 0,
    limit: 10,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationUnitTypes(source.token);

    return () => {
      source.cancel();
    };
  }, [filterState]);

  async function loadAccommodationUnitTypes(token?: CancelToken) {
    if (accommodationUnitTypes !== undefined) {
      setAccommodationUnitTypes(undefined);
    }
    const accommodationUnitTypesRes = await GetAccommodationUnitTypes(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      setAccommodationUnitTypes([]);
      return;
    }

    setAccommodationUnitTypes(accommodationUnitTypesRes.data);
  }

  return (
    <Page className="mt-4">
      <Card
        title="Unit Types"
        subtitle="Manage your accommodation unit types here"
        titleActions={[
          {
            action: () => history.push("/admin/accommodation/unit-types/new"),
            content: (
              <>
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: "6px" }} />
                {t("New Unit Type")}
              </>
            ),
            buttonType: "primary",
          },
        ]}
      >
        <div style={{ marginTop: "24px" }}>
          <DataTable
            noRoundedTop
            columns={[
              { label: t("Title") },
              { label: t("Base Occupancy") },
              { label: t("Max Occupancy") },
              { label: "" },
            ]}
            loading={accommodationUnitTypes === undefined}
            footer={{
              tableLimit: filterState?.limit,
              tableOffset: filterState?.offset,
              handleTableOffset: direction =>
                setFilterState(prev => ({
                  ...prev,
                  offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
                })),
              disableNextOffset: !accommodationUnitTypes || !(accommodationUnitTypes?.length === filterState?.limit),
            }}
          >
            {accommodationUnitTypes?.map((type: IAccommodationUnitType, index: number) => {
              return (
                <tr
                  key={index}
                  onClick={() => history.push("/admin/accommodation/unit-types/" + String(type?.id))}
                  className="clickable"
                >
                  <td>{type?.title}</td>
                  <td>{type?.base_occupancy}</td>
                  <td>{type?.max_occupancy}</td>
                  <td className="text-right">
                    <FontAwesomeIcon icon={faPencil} />
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </div>
      </Card>
    </Page>
  );
}
